import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service } from '@/services/pmt2Service';
import { Timestamp } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from "@/lib/utils"; // Import cn utility

interface FullMarginHistoryDashboardProps {
  product: PMT2Product | null;
}

// Interface uses string keys matching service output
interface MarginHistoryEntry {
  id: string;
  date: Timestamp;
  'Price': number | null;
  'COGS': number | null;
  'FBA Fee': number | null;
  'Referral Fee %': number | null;
  'Gross Profit Override': number | null;
  'Target ACoS Override': number | null;
  'Gross Profit Buffer %': number;
  'Final Gross Profit $': number | null;
  'Final Target ACoS': number | null;
  changedFields: string[];
  change: string;
  changeDetails?: string;
}

/**
 * Full Margin History Dashboard Component
 */
const FullMarginHistoryDashboard: React.FC<FullMarginHistoryDashboardProps> = ({ product }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [historyEntries, setHistoryEntries] = useState<MarginHistoryEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<MarginHistoryEntry | null>(null);
  const [isChangeDialogOpen, setIsChangeDialogOpen] = useState<boolean>(false);

  const fetchMarginHistory = useCallback(async (companyName: string, asin: string) => {
    setHistoryEntries([]);
    setError(null);
    setLoading(true);
    try {
      const entries = await pmt2Service.getASINMarginHistory(companyName, asin);
      console.log("Raw Margin History Data:", JSON.stringify(entries, null, 2)); // Keep for debugging

      if (product && product.companyName === companyName && product.id === asin) {
        setHistoryEntries(entries as MarginHistoryEntry[]);
      }
    } catch (err) {
       console.error("Error fetching margin history:", err);
      setError('Failed to load margin history.');
    } finally {
      setLoading(false);
    }
  }, [product]);

  useEffect(() => {
    if (product) {
      fetchMarginHistory(product.companyName, product.id);
    } else {
       setHistoryEntries([]);
       setError(null);
       setLoading(false);
    }
  }, [product, fetchMarginHistory]);


  // --- Helper Functions ---
  const formatDate = (timestamp: Timestamp | null): string => {
    if (!timestamp?.toDate) return '-';
    try {
      const date = timestamp.toDate();
      const month = (date.getUTCMonth() + 1).toString();
      const day = date.getUTCDate().toString();
      const year = date.getUTCFullYear().toString().slice(-2);
      return `${month}/${day}/${year}`;
    } catch { return 'Invalid'; }
  };

   const parseSafeFloat = (value: any): number | null => {
      if (value === null || value === undefined || value === '') return null;
      const cleanedValue = String(value).replace(/[$,%]/g, '').replace(/,/g, '');
      const num = parseFloat(cleanedValue);
      return isNaN(num) ? null : num;
  };

  const formatCurrency = (value: number | null | undefined, fieldName?: string, decimals = 2): string => {
    const num = parseSafeFloat(value);
    if (fieldName === 'Gross Profit Override' && (num === null || num === 0)) {
        return '$0.00';
    }
    if (num === null) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

  const formatPercentage = (value: number | null | undefined): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
    return `${Math.round(num)}%`;
  };
  // --- End Helpers ---


  const handleOpenChangeDialog = (entry: MarginHistoryEntry) => {
    if (entry.changeDetails) {
      setSelectedEntry(entry);
      setIsChangeDialogOpen(true);
    }
  };

  const headers = [
      'DATE', 'PRICE', 'COGS', 'FBA FEE', 'REFERRAL FEE %',
      'GROSS PROFIT OVERRIDE', 'TARGET ACOS OVERRIDE',
      'FINAL GROSS PROFIT $', 'FINAL TARGET ACOS', 'CHANGE'
  ];

  // --- Render Logic ---
  const renderTableContent = () => {
      if (loading) { /* ... loading state ... */
          return ( <TableRow><TableCell colSpan={headers.length} className="h-24 text-center"><div className="flex justify-center items-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div><span className="ml-2">Loading...</span></div></TableCell></TableRow> );
      }
      if (error) { /* ... error state ... */
           return ( <TableRow><TableCell colSpan={headers.length} className="text-center text-red-600 py-4">{error}</TableCell></TableRow> );
      }
      if (historyEntries.length === 0) { /* ... empty state ... */
           return ( <TableRow><TableCell colSpan={headers.length} className="text-center text-gray-500 py-4">No margin history found.</TableCell></TableRow> );
      }

      const highlightClass = "bg-yellow-200 dark:bg-yellow-700/50 font-semibold";

      return historyEntries.map((entry, index) => {
          const didChange = (fieldName: string) => entry.changedFields?.includes(fieldName);

          // Determine if derived values should be highlighted
          const shouldHighlightProfit = didChange('Price') || didChange('COGS') || didChange('FBA Fee') || didChange('Referral Fee %') || didChange('Gross Profit Override');
          const shouldHighlightAcos = shouldHighlightProfit || didChange('Target ACoS Override');

          return (
              <TableRow key={`entry_${index}_${entry.id || Math.random()}`}
                        className={cn(
                            index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700/50',
                            'hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-150' // Added hover effect
                        )}>
                  {/* Date */}
                  <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{formatDate(entry.date)}</TableCell>
                  {/* Price */}
                  <TableCell className={cn("px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100", didChange('Price') && highlightClass)}>{formatCurrency(entry['Price'])}</TableCell>
                  {/* COGS */}
                  <TableCell className={cn("px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100", didChange('COGS') && highlightClass)}>{formatCurrency(entry['COGS'])}</TableCell>
                  {/* FBA Fee */}
                  <TableCell className={cn("px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100", didChange('FBA Fee') && highlightClass)}>{formatCurrency(entry['FBA Fee'])}</TableCell>
                  {/* Referral Fee % */}
                  <TableCell className={cn("px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100", didChange('Referral Fee %') && highlightClass)}>
                      {formatPercentage(entry['Referral Fee %'] !== null ? entry['Referral Fee %'] * 100 : null)}
                  </TableCell>
                  {/* Gross Profit Override */}
                  <TableCell className={cn("px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100", didChange('Gross Profit Override') && highlightClass)}>
                      {formatCurrency(entry['Gross Profit Override'], 'Gross Profit Override')}
                  </TableCell>
                  {/* Target ACoS Override */}
                  <TableCell className={cn("px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100", didChange('Target ACoS Override') && highlightClass)}>{formatPercentage(entry['Target ACoS Override'])}</TableCell>
                  {/* Final Gross Profit $ */}
                  <TableCell className={cn("px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100", shouldHighlightProfit && highlightClass)}>
                      {formatCurrency(entry['Final Gross Profit $'])}
                  </TableCell>
                  {/* Final Target ACoS */}
                  <TableCell className={cn("px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100", shouldHighlightAcos && highlightClass)}>
                      {formatPercentage(entry['Final Target ACoS'])}
                  </TableCell>
                  {/* Change Button - No highlight */}
                  <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {entry.change ? (
                          <button onClick={() => handleOpenChangeDialog(entry)} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">Change</button>
                      ) : '-'}
                  </TableCell>
              </TableRow>
          );
      });
  };


  return (
    <Card>
      <CardHeader className="p-4">
        <CardTitle className="text-2xl font-bold">Full Margin History</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <TableHeader className="bg-gray-50 dark:bg-gray-700">
              <TableRow>
                {headers.map(header => (
                  <TableHead key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {renderTableContent()}
            </TableBody>
          </Table>
        </div>
      </CardContent>

       {/* Change Details Dialog */}
       <Dialog open={isChangeDialogOpen} onOpenChange={setIsChangeDialogOpen} modal={true}>
         <DialogContent className="sm:max-w-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
           <DialogHeader className="border-b border-gray-200 dark:border-gray-700 pb-3">
             <DialogTitle className="text-gray-900 dark:text-gray-100">
               {selectedEntry && `Changes for ${formatDate(selectedEntry.date)}`}
             </DialogTitle>
             {product && (
               <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                 <div><span className="font-medium">Company:</span> {product.companyName}</div>
                 <div><span className="font-medium">ASIN:</span> {product.id}</div>
               </div>
             )}
           </DialogHeader>
           <div className="p-4 mt-1">
             {selectedEntry && selectedEntry.changeDetails ? (
               <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">{selectedEntry.changeDetails}</p>
             ) : (
               <p className="text-sm text-gray-500 dark:text-gray-400">No specific changes recorded for this date.</p>
             )}
           </div>
         </DialogContent>
       </Dialog>
    </Card>
  );
};

export default FullMarginHistoryDashboard;